import angular from 'angular';
import { chain, debounce, find, keys, map, sortedIndex, throttle } from 'lodash';

// A common controller shared between the market and admin style guides.
//
// Handles keeping track of the tree of styleguide modules, via scroll and
// resize handlers. Also provides functions to scroll the window to any module.
//
// To use: use `resolve` to define the following properties:
//   * `moduleTree`
//   * `moduleListContainerId` (the scrolling container that has the full list
//      of rendered modules in the full-list view of the styleguide. Or `null`
//      to use the full-page/document scroll instead.)
//   * `moduleScrollOffsetTop` (extra amount to scroll when jumping to a module
//      in the list, just for aesthetics)
//
// This controller will then dependency-inject those properties and make use of
// them. It will transform `moduleTree` and expose it to be used by the
// templates, for example.
module.exports = angular.module('common.controllers.style-list', [])
.controller('commonStyleListCtrl', class {
  constructor(moduleTree, moduleListContainerId, moduleScrollOffsetTop,
              $scope, $stateParams, $location, $anchorScroll, $document) {
    if (moduleListContainerId) {
      this.listContainerEl =
        angular.element(document.getElementById(moduleListContainerId));
    } else {
      this.listContainerEl = $document;
    }

    this.moduleScrollOffsetTop = moduleScrollOffsetTop;

    this.moduleTree = moduleTree;
    this.groupNames = keys(this.moduleTree);
    this.flatModuleNames = chain(this.groupNames).map((groupName) => {
      return [groupName].concat(this.moduleTree[groupName]);
    }).flatten().value();

    this.moduleParents = {};
    this.groupNames.forEach((groupName) => {
      this.moduleParents[groupName] = groupName;
      this.moduleTree[groupName].forEach((moduleName) => {
        this.moduleParents[moduleName] = groupName;
      });
    });

    this.selectedModuleName = null;
    this.expandedGroupName = null;

    // On scroll, we check how our scroll position compares to the module
    // offsets, so that we can display our position in the right navbar.
    let throttledOnScroll = throttle(() => {
      this.selectedModuleName = this.getSelectedModuleName();
      this.expandedGroupName = this.moduleParents[this.selectedModuleName];

      $scope.$digest();
    }, 10);

    // Every now and then on scroll, we recompute the module offsets from the
    // top of the page. This is just in case their heights changed (e.g. if you
    // selected a different example that took up more height), so that we can
    // continue to track the position on scroll.
    let debouncedRebuild = debounce(() => {
      this.recomputeOffsets();
    }, 300, { leading: true });

    this.listContainerEl.on('scroll', throttledOnScroll);
    this.listContainerEl.on('scroll', debouncedRebuild);
    $scope.$on('$destroy', () => {
      this.listContainerEl.off('scroll', throttledOnScroll);
      this.listContainerEl.off('scroll', debouncedRebuild);
    });

    this.recomputeOffsets();
  }

  scrollToAnchor(anchor) {
    // It's important to recompute the module offsets from the top of the page
    // at the last minute, in case they changed (e.g. due to late rendering)
    // but you didn't scroll. This is particularly important for the buttons at
    // the top of the page.
    this.recomputeOffsets();
    let offset = find(this.moduleOffsets, { name: anchor }).offset || 0;

    // Scroll a little extra to make sure the sidebar displays us as "in" the
    // section we scrolled to
    this.listContainerEl.scrollTop(offset + 4);
  }

  // Special case for when the `listContainerEl` is `$document`, which doesn't
  // have a `getBoundingClientRect` function
  getListContainerTopOffset() {
    if (this.listContainerEl[0].getBoundingClientRect) {
      return this.listContainerEl[0].getBoundingClientRect().top;
    } else {
      return 0;
    }
  }

  // Returns a list of { name, offset } for the flattened list of modules
  recomputeOffsets() {
    this.moduleOffsets = map(this.flatModuleNames, (moduleName) => {
      let moduleEl = document.getElementById(moduleName);
      let offsetFromPageTop = null;
      if (moduleEl) {
        offsetFromPageTop = moduleEl.getBoundingClientRect().top -
          this.getListContainerTopOffset() +
          this.listContainerEl.scrollTop() +
          this.moduleScrollOffsetTop;
      }
      return {
        name: moduleName,
        offset: offsetFromPageTop
      };
    });
    // For performance reasons, calculate this on rebuild so we can search
    // through it using _.sortedIndex
    // TODO(lodash-4): Use sortedIndexBy and remove this array.
    this.moduleOffsetsNumArray =
        this.moduleOffsets.map((moduleOffset) => moduleOffset.offset);
  }

  getSelectedModuleName() {
    let scrollTop = this.listContainerEl.scrollTop();
    // First module index after the current scroll position
    // TODO(lodash-4): Use sortedIndexBy
    let firstModuleIndex = sortedIndex(this.moduleOffsetsNumArray, scrollTop);

    // Subtract 1 and constrain to (0, length - 1)
    firstModuleIndex = Math.min(Math.max(firstModuleIndex - 1, 0), this.moduleOffsets.length - 1);
    return this.flatModuleNames[firstModuleIndex];
  }
})
.name;



/** WEBPACK FOOTER **
 ** ./javascripts/common/controllers/style-list.js
 **/